import { useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteBreeding } from "../../../../api/breeding";
import { Link, useNavigate, useParams } from "react-router-dom";
import { enumsectionToI18nKey } from "../../../../generated/enums";
import { useAccountStorage } from "../../../../hooks/useAccountStorage";
import { getDevicesForBuilding } from "../../../../utils/breedingDevices";
import { spaceForType } from "../../../../utils/roles";
import { Header, HeaderAction } from "../../../Header";
import { sectionToIcon } from "../../../type/default";
import { DeleteEmptyModal } from "../DeleteEmptyModal";
import { AssociateDeviceModal } from "../../device/AssociateDeviceModal";
import { setupBreedingPath } from "../../setup/setupRoutes";
import { AddBuildingCard } from "./AddBuildingCard";
import { BreedingInfoCard } from "./BreedingInfoCard";
import { BreedingPartnersCard } from "./BreedingPartnersCard";
import {
  BREEDER_BREEDINGS_PATH,
  INSTALLER_BREEDINGS_PATH,
} from "./breedingRoutes";
import { BuildingCard } from "./BuildingCard";
import { ClientCard } from "./ClientCard";
import {
  breedingNamesOf,
  UnassignedDevicesCard,
} from "./UnassignedDevicesCard";
import { useFarmerBreedings } from "./useFarmerBreedings";

export const BreedingDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const breedingId = Number(useParams().breedingId);
  const { breedings, unassignedDevices, isLoading, hasError, reload } =
    useFarmerBreedings();
  const [isAssociateDeviceOpen, setIsAssociateDeviceOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { getAccountStorage } = useAccountStorage();
  const accountType = getAccountStorage()?.type;
  // Compte de consultation : un collaborateur éleveur ne crée ni ne modifie rien, et n'a
  // pas accès aux partenariats.
  const isOwner = accountType === "breeder";
  // Installateur : la fiche d'un élevage client, modifiable seulement s'il lui est partagé
  // en entier (droit calculé par l'API).
  const isInstaller = spaceForType(accountType) === "installer";
  const canAssociateDevice = isOwner || isInstaller;
  const listPath = isInstaller
    ? INSTALLER_BREEDINGS_PATH
    : BREEDER_BREEDINGS_PATH;

  const entry = breedings.find((item) => item.breeding.id === breedingId);

  // Tant que l'élevage n'est pas là, l'en-tête garde un titre générique.
  if (isLoading || hasError || !entry) {
    return (
      <div className="p-4 space-y-4">
        <Header
          title={t(enumsectionToI18nKey["section_breeding"])}
          icon={sectionToIcon.section_breeding}
        />
        {isLoading ? (
          <p className="text-tertiary">{t("loading")}</p>
        ) : (
          <div className="space-y-3">
            <p className="text-red-500">
              {hasError
                ? t("breeding-detail-load-error")
                : t("breeding-detail-not-found")}
            </p>
            <Link to={listPath} className="text-secondary underline">
              {t("breeding-detail-back")}
            </Link>
          </div>
        )}
      </div>
    );
  }

  // Avec un seul élevage, la liste de l'éleveur est sautée (cf. BreedingPage) : ses
  // actions, ses produits sans bâtiment et le retour vers elle sont donc portés ici.
  const isOnlyBreeding = !isInstaller && breedings.length === 1;
  const openAssociateDevice = canAssociateDevice
    ? () => setIsAssociateDeviceOpen(true)
    : undefined;

  return (
    <div className="p-4 space-y-4">
      <Header
        title={entry.breeding.name}
        icon={sectionToIcon.section_breeding}
        actions={
          <>
            {!isOnlyBreeding && (
              <HeaderAction
                icon="arrow-left"
                variant="secondary"
                label={t("breeding-detail-back")}
                onClick={() => navigate(listPath)}
              />
            )}
            {isOwner && isOnlyBreeding && (
              <HeaderAction
                icon="plus"
                label={t("breeding-create")}
                onClick={() => navigate(setupBreedingPath())}
              />
            )}
            {canAssociateDevice && (
              <HeaderAction
                icon="link"
                variant="secondary"
                label={t("device-link-title")}
                onClick={() => setIsAssociateDeviceOpen(true)}
              />
            )}
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] items-start">
        <div className="space-y-4">
          <BreedingInfoCard
            key={entry.breeding.id}
            breeding={entry.breeding}
            isPartial={entry.isPartial}
            canEdit={entry.canEdit}
            canDelete={isOwner}
            onSaved={reload}
            onDelete={() => setIsDeleteOpen(true)}
          />
          {isOwner && <BreedingPartnersCard breedingId={entry.breeding.id} />}
          {isInstaller && (
            <ClientCard company={entry.company} accounts={entry.accounts} />
          )}
        </div>

        <div className="space-y-4">
          {entry.buildings.length === 0 && (
            <p className="text-sm text-grey-medium text-center py-4">
              {t("breeding-detail-no-building")}
            </p>
          )}
          {entry.buildings.map((building) => (
            <BuildingCard
              key={building.id}
              building={building}
              devices={getDevicesForBuilding(building, entry.devices)}
              canEdit={entry.canEdit}
              canDelete={isOwner}
              onSaved={reload}
              onAssociateDevice={openAssociateDevice}
              licenseCompanyId={isInstaller ? entry.breeding.id_company : 0}
            />
          ))}
          {entry.canEdit && (
            <AddBuildingCard
              breedingId={entry.breeding.id}
              onCreated={reload}
            />
          )}
          {isOnlyBreeding && (
            <UnassignedDevicesCard
              groups={unassignedDevices}
              breedingNames={breedingNamesOf(breedings)}
              onAttached={reload}
              showCompany={false}
            />
          )}
        </div>
      </div>

      {canAssociateDevice && (
        <AssociateDeviceModal
          isOpen={isAssociateDeviceOpen}
          onClose={() => setIsAssociateDeviceOpen(false)}
          onAssociated={reload}
          companyId={isInstaller ? entry.breeding.id_company : undefined}
        />
      )}

      {isOwner && (
        <DeleteEmptyModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          title={t("breeding-delete")}
          warningText={t("breeding-delete-warning", {
            name: entry.breeding.name,
          })}
          blockedText={
            entry.buildings.length > 0 ? t("breeding-delete-not-empty") : ""
          }
          onDelete={() => deleteBreeding(entry.breeding.id)}
          onDeleted={() => navigate(BREEDER_BREEDINGS_PATH, { replace: true })}
        />
      )}
    </div>
  );
};
